import { AuthContext } from "@/hooks/auth/AuthContext";
import { readBuildingSetupDraft } from "../buildingSetupDraft";
import { paidBuildingSetupNeeded, paymentMethodForBillingSetup } from "../buildingPlanCreation";
import { capture } from "@/lib/sentry/capture";
import { useContext, useEffect, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Info, Loader2 } from "lucide-react";
import { useForm, useWatch, type UseFormReturn } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Translations } from "@/lib/i18n/types";
import { useBillingStatus } from "../BillingStatusContext";
import { useBillingRefresh, useConfirmPaymentMethodSetup, useSaveBillingDetails } from "../hooks";
import { billingErrorField, billingErrorMessage } from "../messages";
import {
  isAutomaticPaymentMethod,
  paymentMethodSchema,
  type BillingContext,
  type BillingDetailsInput,
} from "../types";
import { countryName, normaliseVatNumber, vatNumberIssue } from "../vat";
import { PaymentMethodField } from "./PaymentMethodField";
import { useStartSetup } from "../useStartSetup";
import { RestrictedActionButton } from "./RestrictedActionButton";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

/**
 * Length caps, mirroring `billing-rules.ts` in the Edge Function exactly.
 *
 * The server is the boundary; these exist so an over-long value fails on the
 * field that is too long instead of round-tripping and coming back as the
 * generic "validation" sentence with nothing to point at.
 */
const MAX_LENGTHS = {
  companyName: 200,
  registryCode: 200,
  legalAddress: 500,
  invoiceEmail: 254,
  purchaseOrderNumber: 100,
} as const;

/**
 * The invoice-email rule, character for character the Edge Function's `EMAIL`.
 *
 * Zod's own `.email()` is a different, stricter regex — it demands a two-letter
 * final label, which the server does not — and a client rule the server would
 * have accepted is a field the customer cannot save for no reason they can see.
 */
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * The VAT rule, per country, mirroring the Edge Function's own table.
 *
 * The shape-only rule this replaces accepted `EE1232412313` — ten digits where
 * Estonia issues nine — and the refusal came back from Stripe, as a generic
 * failure the owner could do nothing with. The country decides the format, and
 * the prefix is the only country signal this form carries, so the message names
 * the country and quotes the shape Stripe documents for it.
 */
function vatIssueMessage(value: string, t: Translations, language: string): string | null {
  const issue = vatNumberIssue(value);
  if (!issue) return null;
  if (issue.kind === "prefix") return t.billing.details.errors.vatPrefix;
  return fill(t.billing.details.errors.vatCountryFormat, {
    country: countryName(issue.country, language),
    example: issue.example,
  });
}

function detailsFormSchema(t: Translations, language: string) {
  const errors = t.billing.details.errors;
  const tooLong = (message: string, max: number) => fill(message, { max: String(max) });
  return z.object({
    companyName: z
      .string()
      .trim()
      .min(1, errors.companyRequired)
      .max(MAX_LENGTHS.companyName, tooLong(errors.companyTooLong, MAX_LENGTHS.companyName)),
    registryCode: z
      .string()
      .trim()
      .max(MAX_LENGTHS.registryCode, tooLong(errors.registryTooLong, MAX_LENGTHS.registryCode)),
    vatNumber: z.string().superRefine((value, ctx) => {
      const message = vatIssueMessage(value, t, language);
      if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    }),
    legalAddress: z
      .string()
      .trim()
      .min(1, errors.addressRequired)
      .max(MAX_LENGTHS.legalAddress, tooLong(errors.addressTooLong, MAX_LENGTHS.legalAddress)),
    invoiceEmail: z
      .string()
      .trim()
      .regex(EMAIL, errors.emailInvalid)
      .max(MAX_LENGTHS.invoiceEmail, tooLong(errors.emailTooLong, MAX_LENGTHS.invoiceEmail)),
    purchaseOrderNumber: z
      .string()
      .max(
        MAX_LENGTHS.purchaseOrderNumber,
        tooLong(errors.purchaseOrderTooLong, MAX_LENGTHS.purchaseOrderNumber)
      ),
    paymentMethod: paymentMethodSchema,
  });
}

function textOrEmpty(value: string | null | undefined): string {
  return value ?? "";
}

function formValues(context: BillingContext): BillingDetailsInput {
  const details = context.details;
  return {
    companyName: textOrEmpty(details.company_name),
    registryCode: textOrEmpty(details.registry_code),
    vatNumber: textOrEmpty(details.vat_number),
    legalAddress: textOrEmpty(details.legal_address),
    invoiceEmail: textOrEmpty(details.invoice_email),
    purchaseOrderNumber: textOrEmpty(details.purchase_order_number),
    // Null for an organisation that has never saved details (and for a caller
    // the RPC withholds the block from, who cannot reach this form anyway).
    // Card is the recommended method, so it is what an unanswered picker
    // proposes — the customer still has to save before anything is charged.
    paymentMethod: paymentMethodForBillingSetup(context),
  };
}

/**
 * Why the form is read-only, or undefined when it is editable.
 *
 * Read-only organisations are deliberately NOT blocked here: settling the
 * overdue invoice can require fixing the invoice email or the company data, so
 * locking this form would lock the owner out of the only way back.
 */
function editingRestriction(context: BillingContext, t: Translations): string | undefined {
  if (!context.is_org_owner) return t.billing.details.ownerOnly;
  return undefined;
}

function FieldError({ message }: { message?: string }) {
  return message ? <p className="text-sm text-destructive">{message}</p> : null;
}

interface BillingFieldsProps {
  form: UseFormReturn<BillingDetailsInput>;
  disabled: boolean;
}

function CompanyFields({ form, disabled }: BillingFieldsProps) {
  const { t } = useLanguage();
  const copy = t.billing.details;
  return (
    <>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor="billing-company-name">{copy.companyName}</Label>
        <Input id="billing-company-name" disabled={disabled} {...form.register("companyName")} />
        <FieldError message={form.formState.errors.companyName?.message} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="billing-registry-code">{copy.registryCode}</Label>
        <Input
          id="billing-registry-code"
          placeholder={copy.optional}
          maxLength={MAX_LENGTHS.registryCode}
          disabled={disabled}
          {...form.register("registryCode")}
        />
        <FieldError message={form.formState.errors.registryCode?.message} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="billing-vat-number">{copy.vatNumber}</Label>
        <Input
          id="billing-vat-number"
          placeholder={copy.vatPlaceholder}
          disabled={disabled}
          {...form.register("vatNumber")}
        />
        <FieldError message={form.formState.errors.vatNumber?.message} />
      </div>
    </>
  );
}

function LegalAddressField({ form, disabled }: BillingFieldsProps) {
  const { t } = useLanguage();
  return (
    <div className="space-y-2 sm:col-span-2">
      <Label htmlFor="billing-legal-address">{t.billing.details.legalAddress}</Label>
      <Input id="billing-legal-address" disabled={disabled} {...form.register("legalAddress")} />
      <FieldError message={form.formState.errors.legalAddress?.message} />
    </div>
  );
}

function InvoiceReferenceFields({ form, disabled }: BillingFieldsProps) {
  const { t } = useLanguage();
  const copy = t.billing.details;
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="billing-invoice-email">{copy.invoiceEmail}</Label>
        <Input
          id="billing-invoice-email"
          type="email"
          placeholder={copy.invoiceEmailPlaceholder}
          disabled={disabled}
          {...form.register("invoiceEmail")}
        />
        <FieldError message={form.formState.errors.invoiceEmail?.message} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="billing-po-number">{copy.purchaseOrderNumber}</Label>
        <Input
          id="billing-po-number"
          placeholder={copy.purchaseOrderPlaceholder}
          disabled={disabled}
          {...form.register("purchaseOrderNumber")}
        />
        <p className="text-sm text-muted-foreground">{copy.purchaseOrderHint}</p>
      </div>
    </>
  );
}

type SaveDetails = (variables: { orgId: string; input: BillingDetailsInput }) => Promise<unknown>;

/**
 * Save, and put the failure where the person typing can see it.
 *
 * A refused save used to be a toast and nothing else, so "some of the details
 * are not valid" left the owner to guess which field. `billingErrorField` reads
 * the field the Edge Function blamed — falling back to the one its message names
 * — and the message is attached to that input as well as raised as a toast: the
 * toast is what you notice, the field error is what you fix.
 *
 * `mutateAsync` only resolves once the billing queries have been re-read (see
 * `useBillingRefresh`), so resetting the form here leaves it holding exactly what
 * is now saved — no stale defaults, and Save correctly disabled again.
 */
async function submitDetails(
  save: SaveDetails,
  orgId: string,
  input: BillingDetailsInput,
  t: Translations,
  form: UseFormReturn<BillingDetailsInput>
) {
  const saved = { ...input, vatNumber: normaliseVatNumber(input.vatNumber) };
  try {
    await save({ orgId, input: saved });
    form.reset(saved);
    toast.success(t.billing.details.saved);
    return true;
  } catch (error) {
    const message = billingErrorMessage(error, t, t.billing.details.saveFailed);
    const field = billingErrorField(error);
    if (field) form.setError(field, { type: "server", message });
    toast.error(message);
    return false;
  }
}

/**
 * Handle the return leg of Stripe Checkout, once.
 *
 * Stripe appends `billing_setup=success&session_id=cs_…` to the success URL and
 * `billing_setup=cancelled` to the cancel one (the `{CHECKOUT_SESSION_ID}`
 * template variable is substituted by Stripe — verified against
 * docs.stripe.com/payments/checkout/custom-success-page). The parameters are
 * stripped as soon as they are read, so a reload or a shared link cannot
 * replay the confirmation, and a ref keeps React's double-invoked effects from
 * firing it twice before the URL settles.
 *
 * `confirm_setup` is a shortcut, not the source of truth: the webhook applies
 * the same mirror update, so a failure here is logged and left to it rather
 * than shown as a failed setup the customer actually completed.
 */
function usePaymentSetupReturn(orgId: string) {
  const { t } = useLanguage();
  const [params, setParams] = useSearchParams();
  const confirm = useConfirmPaymentMethodSetup();
  const refresh = useBillingRefresh();
  const handled = useRef(false);
  const outcome = params.get("billing_setup");
  const sessionId = params.get("session_id");

  // Every value the body reads is in the dependency list; the ref is what makes
  // the extra runs (a new mutation object on each render, a language switch)
  // return immediately instead of confirming twice.
  const confirmSetup = confirm.mutateAsync;
  useEffect(() => {
    if (!outcome || handled.current) return;
    handled.current = true;
    const next = new URLSearchParams(params);
    next.delete("billing_setup");
    next.delete("session_id");
    setParams(next, { replace: true });

    if (outcome !== "success") {
      toast.info(t.billing.details.setupCancelled);
      return;
    }
    toast.success(t.billing.details.setupSaved);
    // The billing state is re-read on EVERY return leg, whatever happens next.
    // The confirmation is a shortcut past Stripe's webhook, not the source of
    // truth, and it has two ways of not running at all — no `session_id` on the
    // URL, or a refused call — both of which used to leave the page showing "No
    // payment method yet" over a method the customer had just set up, until they
    // reloaded by hand. The refresh costs one RPC and is correct in every case:
    // if the webhook has landed it shows the method, and if neither has landed
    // the next refetch does.
    void (async () => {
      try {
        if (sessionId) await confirmSetup({ orgId, sessionId });
      } catch (error) {
        capture(error instanceof Error ? error : new Error("Payment setup confirmation failed"), {
          tags: { feature: "billing", action: "confirm_setup" },
          fingerprint: ["billing", "confirm_setup"],
          context: { billing: { orgId } },
        });
      } finally {
        void refresh();
      }
    })();
  }, [outcome, sessionId, orgId, params, setParams, confirmSetup, refresh, t]);
}

function useBuildingReturn(context: BillingContext, isDirty: boolean, isPending: boolean) {
  const navigate = useNavigate();
  const userId = useContext(AuthContext)?.userId;
  const draft = readBuildingSetupDraft(userId, context.org_id);
  const canReturn = Boolean(
    draft &&
      context.is_org_owner &&
      !context.is_read_only &&
      !isDirty &&
      !paidBuildingSetupNeeded(context, draft.tier)
  );
  useEffect(() => {
    if (canReturn && !isPending) navigate("/buildings?new=1", { replace: true });
  }, [canReturn, isPending, navigate]);
  return { draft, resumeBuilding: () => navigate("/buildings?new=1") };
}

function BillingDetailsActions({
  hasDraft,
  disabled,
  restriction,
  isDirty,
  needsSetup,
  isSaving,
  onReturn,
}: {
  hasDraft: boolean;
  disabled: boolean;
  restriction?: string;
  isDirty: boolean;
  needsSetup: boolean;
  isSaving: boolean;
  onReturn: () => void;
}) {
  const { t } = useLanguage();
  return (
    <div className="flex flex-wrap justify-end gap-3 border-t pt-6">
      {hasDraft && (
        <Button type="button" variant="outline" disabled={disabled} onClick={onReturn}>
          {t.billing.details.returnToBuilding}
        </Button>
      )}
      <RestrictedActionButton
        type="submit"
        disabledReason={restriction}
        disabled={disabled || (!isDirty && !needsSetup)}
        {...pageInfoTarget("billing.saveDetails")}
      >
        {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
        {needsSetup ? t.billing.details.saveAndSetUp : t.billing.details.save}
      </RestrictedActionButton>
    </div>
  );
}

function useBillingDetailsInputs(context: BillingContext) {
  const { t, language } = useLanguage();
  const form = useForm<BillingDetailsInput>({
    resolver: zodResolver(detailsFormSchema(t, language)),
    defaultValues: formValues(context),
  });
  // Refresh untouched fields after setup or a background refetch, while keeping unsaved edits.
  const { isDirty } = form.formState;
  useEffect(() => {
    if (isDirty) return;
    form.reset(formValues(context));
  }, [context, form, isDirty]);
  return form;
}

function BillingDetailsForm({ context }: { context: BillingContext }) {
  const { t } = useLanguage();
  const mutation = useSaveBillingDetails();
  usePaymentSetupReturn(context.org_id);
  const form = useBillingDetailsInputs(context);
  const restriction = editingRestriction(context, t);
  const setup = useStartSetup(context.org_id);
  const picked = useWatch({ control: form.control, name: "paymentMethod" });
  const needsSetup =
    isAutomaticPaymentMethod(picked) && context.payment_method_on_file?.type !== picked;
  const disabled = Boolean(restriction) || mutation.isPending || setup.isPending;
  const beforeSetup = async () => {
    if (restriction || !(await form.trigger())) return false;
    if (
      !form.formState.isDirty &&
      !context.details_missing &&
      picked === context.details.payment_method
    )
      return true;
    return submitDetails(mutation.mutateAsync, context.org_id, form.getValues(), t, form);
  };
  const save = form.handleSubmit(async () => {
    if ((await beforeSetup()) && needsSetup) await setup.start();
  });
  const { draft, resumeBuilding } = useBuildingReturn(
    context,
    form.formState.isDirty,
    mutation.isPending || setup.isPending
  );

  return (
    <div className="space-y-6">
      {restriction && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>{t.billing.details.restrictedTitle}</AlertTitle>
          <AlertDescription>{restriction}</AlertDescription>
        </Alert>
      )}
      <form onSubmit={save} className="space-y-8">
        <div className="grid gap-5 sm:grid-cols-2">
          <CompanyFields form={form} disabled={disabled} />
          <LegalAddressField form={form} disabled={disabled} />
          <InvoiceReferenceFields form={form} disabled={disabled} />
          <PaymentMethodField
            form={form}
            disabled={disabled}
            restriction={restriction}
            context={context}
            onSetup={async () => {
              if (await beforeSetup()) await setup.start();
            }}
          />
        </div>
        <BillingDetailsActions
          hasDraft={Boolean(draft)}
          disabled={disabled}
          restriction={restriction}
          isDirty={form.formState.isDirty}
          needsSetup={needsSetup}
          isSaving={mutation.isPending}
          onReturn={resumeBuilding}
        />
      </form>
    </div>
  );
}

export function BillingDetailsPage() {
  const { t } = useLanguage();
  const { context, isLoading, isError, refetch } = useBillingStatus();
  if (isLoading) return <div className="h-72 animate-pulse bg-muted" />;
  if (isError || !context) {
    return (
      <div className="border-y border-destructive/40 py-6">
        <p className="font-medium">{t.billing.errors.contextTitle}</p>
        <Button className="mt-4" variant="outline" onClick={() => refetch()}>
          {t.billing.errors.retry}
        </Button>
      </div>
    );
  }
  return <BillingDetailsForm key={context.org_id} context={context} />;
}
